import React, { useEffect, useRef, useState } from 'react';
import Imgix from 'react-imgix';
import styled from 'styled-components';
import Icon from '../../../../../common/Icon';
import useOnClickOutside from '../../../../../hooks/useOnClickOutside';
import { StyledCheckbox } from '../../../../../styles/theme/default/others';
import { BodyFour, BodyThree } from '../../../../../styles/theme/default/typography';
import { makeRandomId } from '../../../../../utils/helper';
import { Tooltip } from '../../Styles';
import Cart from '../sidebar/cart/Cart';
import { getRollSqFt } from '../sidebar/wallpaperCalculator/WallpaperCalculator';
import SquareFeetCalculator from './SquareFeetCalculator';

const Wrapper = styled.div`
  border-left: 1px solid #e2e2e2;
  width: 420px;
  right: 0;
  background: white;
  position: fixed;
  top: 0;
  z-index: 1010;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100vh;

  .body-container {
    overflow: auto;
    padding: 25px;
  }

  .how-to-calculate-title {
    margin-bottom: 16px;
  }

  .how-to-calculate-desc {
    margin-bottom: 40px;
  }

  .ten-percent-checkbox {
    margin-top: 32px;
  }

  .ten-percent-txt {
    display: flex;
    align-items: center;
  }

  .tooltip-container {
    margin-left: 8px;
    position: relative;
  }

  .ten-percent {
    .tooltip-customize {
      right: -5px;
      left: unset;

      .tooltip-arrow {
        right: 5px;
        left: unset;
      }

      .desc {
        text-align: start;
      }
    }
    &:hover {
      .tooltip-customize {
        transform: scale(1);
      }
    }
  }

  .close-btn-container {
    margin-top: 15px;
    margin-bottom: 25px;
    button {
      padding-left: 0;
      background: white;
      border: none;
      &:outline {
        border: none;
      }
    }
    img {
      width: 20px;
      height: auto;
    }
  }

  @media (max-width: 992px) {
    border-left: none;
    width: 100%;
    top: 0px;
    height: calc(100% - 0px);

    .body-container {
      padding: 16px;
    }
  }
`;

function HowToCalculateWallpaperSlider({
  handleOpenWallpaper,
  open,
  productData,
  handleToggleSideCart
}) {
  const [rolls, setRolls] = useState(1);
  const [cartQuantity, setCartQuantity] = useState(1);
  const [tenPercentExtra, setTenPercentExtra] = useState(false);
  const [sections, setSections] = useState([
    { id: 'default', height: '', width: '' }
  ]);

  const rollSquareFeet = getRollSqFt(
    productData?.frame?.primarySubcategory?.id
  );

  const ref = useRef(null);

  const totalSqFeet = sections.reduce((a, c) => c.width * c.height + a, 0);

  useEffect(() => {
    const totalSquareFeetWithRolls = rolls * rollSquareFeet;
    const squareFeetWithExtraTenPercent = totalSqFeet * 1.1;
    if (tenPercentExtra) {
      if (squareFeetWithExtraTenPercent > totalSquareFeetWithRolls) {
        //set roles extra rolls
        const numberOfRolls = squareFeetWithExtraTenPercent / rollSquareFeet;
        const roundUpRolls = Math.ceil(numberOfRolls);
        setRolls(roundUpRolls);
        // setCartQuantity(roundUpRolls);
      } else {
        handleRolls();
      }
    } else {
      // remove 10% extra
      handleRolls();
    }
  }, [tenPercentExtra, totalSqFeet]);

  useOnClickOutside(ref, () => {
    if (open) {
      handleOpenWallpaper(false);
    }
  });

  const removeTenPercentOnChange = () => {
    if (tenPercentExtra) {
      setTenPercentExtra(false);
    }
  };

  const handleRolls = () => {
    const numberOfRolls = totalSqFeet / rollSquareFeet;
    const roundUpRolls = Math.ceil(numberOfRolls);
    setRolls(roundUpRolls);
  };

  const handleHeight = e => {
    removeTenPercentOnChange();
    if (isNaN(Number(e.target.value))) return;
    const newSections = [...sections];
    const sectionIndex = newSections.findIndex(x => x.id === e.target.name);
    newSections[sectionIndex].height = e.target.value;

    setSections(newSections);
  };

  const handleWidth = e => {
    removeTenPercentOnChange();
    if (isNaN(Number(e.target.value))) return;
    const newSections = [...sections];
    const sectionIndex = newSections.findIndex(x => x.id === e.target.name);
    newSections[sectionIndex].width = e.target.value;

    setSections(newSections);
  };

  const handleTenPercentExtra = () => {
    setTenPercentExtra(!tenPercentExtra);
  };

  const isWallpaper = productData.frame?.type.id == 4;

  return (
    <Wrapper open={open} ref={ref}>
      <div className="body-container">
        <div className="close-btn-container">
          <button onClick={() => handleOpenWallpaper(false)}>
            <Imgix
              className="close-icon"
              imgixParams={{ auto: 'compress,format' }}
              sizes="50px"
              src={`https://theinside.imgix.net/icons/close-x.svg`}
              htmlAttributes={{
                alt: 'close-icon'
              }}
            />
          </button>
        </div>
        <BodyThree className="how-to-calculate-title" bold={true}>
          How To Calculate
        </BodyThree>
        <BodyThree className="how-to-calculate-desc">
          Enter Height and Width of Wall(s) below, and we will tell you how many
          rolls are required.
        </BodyThree>

        {sections.map((section, i) => {
          return (
            <div key={section.id}>
              <SquareFeetCalculator
                id={section.id}
                height={section.height}
                width={section.width}
                handleHeight={handleHeight}
                handleWidth={handleWidth}
                handleRemove={() => {
                  const newData = sections.filter(x => {
                    return x.id !== section.id;
                  });
                  setSections(newData);
                }}
              />
            </div>
          );
        })}

        <AddAnotherSectionBtn
          onClick={() =>
            setSections([
              ...sections,
              { id: makeRandomId(5), height: '', width: '' }
            ])
          }
        >
          + Add Another Section
        </AddAnotherSectionBtn>

        <StyledCheckbox className="ten-percent-checkbox">
          <input
            type="checkbox"
            checked={tenPercentExtra}
            name="tenPercentExtra"
            onChange={handleTenPercentExtra}
          />
          <span className="checkmark" />
          <BodyFour className="ten-percent-txt">
            Add 10% extra for waste and reserve (Recommended){' '}
            <div className="tooltip-container ten-percent">
              <Icon icon="ti-tooltip-info" size={25} color="#2A5CAF" />
              <Tooltip className="tooltip-customize">
                <div className="tooltip-arrow" />
                <BodyFour className="desc">
                  We recommend an overage of 10% to account for any cuts,
                  corners, waste, or any future replacement needs.
                </BodyFour>
              </Tooltip>
            </div>
          </BodyFour>
        </StyledCheckbox>

        <ResumeBox>
          <div className="item">
            <BodyThree>Total Square Footage</BodyThree>
            <BodyThree>{totalSqFeet}</BodyThree>
          </div>
          <div className="item">
            <BodyThree>Rolls Required ({rollSquareFeet} sq ft./roll)</BodyThree>
            <BodyThree>{rolls}</BodyThree>
          </div>
        </ResumeBox>

        <Cart
          rolls={rolls}
          sourceWallpaperSideBar={true}
          isWallpaper={isWallpaper}
          cartQuantity={rolls}
          setCartQuantity={setCartQuantity}
          handleToggleSideCart={handleToggleSideCart}
          productData={productData}
          price={productData.price}
          isPurchasable={productData.is_purchasable}
        />
      </div>
    </Wrapper>
  );
}

export default HowToCalculateWallpaperSlider;

const ResumeBox = styled.div`
  margin-top: 32px;
  margin-bottom: 45px;

  background: ${({ theme }) => theme.colors.oatmilk};
  padding: 13px;

  .item {
    display: flex;
    justify-content: space-between;
  }
`;

const AddAnotherSectionBtn = styled.button`
  border: 1px solid black;
  width: 100%;
  height: 48px;
  background: white;
`;
